import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { GET_CITIES } from '@/core/graphql/queries/getCities';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { City } from '@/core/models/locations/city';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import Head from 'next/head';
import Link from 'next/link';
import { GetStaticPaths, GetStaticProps } from 'next';

const BIKE_PARTS_CATEGORY_ID = '3038';

const POPULAR_BIKE_PATHS = [
    { make: 'Honda', model: 'CBR600' },
    { make: 'Honda', model: 'CB500' },
    { make: 'Honda', model: 'Africa Twin' },
    { make: 'Yamaha', model: 'MT-07' },
    { make: 'Yamaha', model: 'R1' },
    { make: 'Yamaha', model: 'R6' },
    { make: 'Kawasaki', model: 'Ninja 650' },
    { make: 'Kawasaki', model: 'Z900' },
    { make: 'Suzuki', model: 'GSX-R600' },
    { make: 'Suzuki', model: 'V-Strom' },
    { make: 'BMW', model: 'GS' },
    { make: 'KTM', model: 'Duke 390' },
    { make: 'KTM', model: 'Adventure' },
    { make: 'Ducati', model: 'Monster' },
    { make: 'Harley-Davidson', model: 'Sportster' },
];

const TOP_CITIES = [
    'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Gqeberha',
    'Bloemfontein', 'East London', 'Polokwane', 'Nelspruit',
    'Roodepoort', 'Benoni', 'Boksburg', 'Germiston', 'Randburg', 'Sandton',
];

interface Props {
    businesses: BusinessItem[];
    pagination: { count: number; currentPage: number; totalPages: number };
    make: string;
    makeDisplay: string;
    model: string;
    modelDisplay: string;
    city: string;
    cityName: string;
}

export default function BikePartsMakeModelCityPage({
    businesses, pagination, make, makeDisplay, model, modelDisplay, city, cityName,
}: Props) {
    const searchParams = {
        category: BIKE_PARTS_CATEGORY_ID,
        text: `${makeDisplay} ${modelDisplay}`,
        city, province: '', suburb: '', page: 1,
    };

    const pageTitle = `${makeDisplay} ${modelDisplay} Bike Parts in ${cityName} | Motorcycle Spares`;
    const pageDescription = `Find ${makeDisplay} ${modelDisplay} motorcycle parts and bike spares in ${cityName}. Browse trusted suppliers of new and used ${makeDisplay} ${modelDisplay} parts near you.`;
    const canonicalUrl = `${ENV.DOMAIN_URL}bike-parts/${make}/${model}/${city}/`;

    const keywords = [
        `${makeDisplay} ${modelDisplay} parts ${cityName}`,
        `${makeDisplay} ${modelDisplay} motorcycle spares ${cityName}`,
        `buy ${makeDisplay} ${modelDisplay} bike parts South Africa`,
        `motorcycle parts ${cityName}`,
        `bike spares ${cityName}`,
    ].join(', ');

    const breadcrumbs = [
        { label: 'Bike Parts', href: '/bike-parts/' },
        { label: makeDisplay, href: `/bike-parts/${make}/` },
        { label: modelDisplay, href: `/bike-parts/${make}/${model}/` },
        { label: cityName, href: `/bike-parts/${make}/${model}/${city}/` },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
            </Head>

            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div className="absolute inset-0 opacity-35" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(220, 38, 38, 0.35), transparent 45%)' }} />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <nav className="text-sm text-red-200 mb-3">
                                {breadcrumbs.map((crumb, i) => (
                                    <span key={crumb.href}>
                                        {i > 0 && <span className="mx-1">&gt;</span>}
                                        {i < breadcrumbs.length - 1
                                            ? <Link href={crumb.href} className="hover:text-white">{crumb.label}</Link>
                                            : <span className="text-white">{crumb.label}</span>}
                                    </span>
                                ))}
                            </nav>
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">Motorcycle Parts Directory</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                {makeDisplay} {modelDisplay} Parts in {cityName}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                Find quality {makeDisplay} {modelDisplay} bike parts and motorcycle spares from trusted
                                suppliers in {cityName}, South Africa.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link href="#business-filters" className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow">Find Suppliers</Link>
                                <Link href="/bike-parts/" className="border border-white/30 text-white px-5 py-2.5 rounded-md text-sm font-semibold">All Bike Parts</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="my-10" id="business-filters">
                <div className="container">
                    <BusinessList searchParams={searchParams} initialBusinesses={businesses} initialPagination={pagination} />
                </div>
            </div>
            <DownloadApp />
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    const apolloClient = initializeApollo();
    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];
    const includedCities = cities
        .filter((c: City) => c.include === true || c.include === 1)
        .filter((c: City) => TOP_CITIES.includes(c.cityName));

    const paths: { params: { make: string; model: string; city: string } }[] = [];
    for (const entry of POPULAR_BIKE_PATHS) {
        for (const city of includedCities) {
            paths.push({ params: { make: slugify(entry.make), model: slugify(entry.model), city: slugify(city.cityName) } });
        }
    }
    return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const apolloClient = initializeApollo();
    const makeSlug = params?.make as string;
    const modelSlug = params?.model as string;
    const citySlug = params?.city as string;

    const { data: cityData } = await apolloClient.query({ query: GET_CITIES });
    const cities: City[] = cityData?.getCity?.result || [];
    const city = cities.find((c: City) => slugify(c.cityName) === citySlug);
    if (!city) return { notFound: true };

    const toDisplay = (s: string) => s.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const businessRequest: SearchBusinessRequest = {
        statusIds: '1', domainId: Number(ENV.CATEGORY_ID), categoryIds: BIKE_PARTS_CATEGORY_ID,
        provinceIds: '', cityIds: String(city.cityId), suburbIds: '', companyName: '', page: 1, size: 12,
    };

    const { data: businessData } = await apolloClient.query({ query: GET_BUSINESS_LIST, variables: businessRequest });
    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: { count: businessData?.getBusinessList?.count ?? 0, currentPage: businessData?.getBusinessList?.currentPage ?? 1, totalPages: businessData?.getBusinessList?.totalPages ?? 0 },
            make: makeSlug, makeDisplay: toDisplay(makeSlug),
            model: modelSlug, modelDisplay: toDisplay(modelSlug),
            city: citySlug, cityName: city.cityName,
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
